import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  Divider,
  Paper,
  Avatar,
  Dialog,
  IconButton,
  CircularProgress,
  Container,
  useTheme,
  Tooltip,
  Button,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';
import Slider from "react-slick";
import { request } from "../../../../../utils/request";
import {
  LocalGasStation,
  Settings,
  DirectionsCar,
  CalendarToday,
} from "@mui/icons-material";
import Brandcrumb from "../../../../../componentsUiBlock/Brandcrumb";
import { Car } from "../../../types";
import { baseUrlImage } from "../../../../../utils/apiClient";
import { shadowImage } from "../CarsList";

const CarInfo: React.FC = () => {
  const { id } = useParams();
  const [car, setCar] = useState<Car | null>(null);
  const [open, setOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [startIndex, setStartIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  // const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    const fetchCar = async () => {
      try {
        setLoading(true);
        const res = await request({
          url: `/compatibility/get-car-model/${id}`,
          method: "get",
        });
        if (res.carModel) setCar(res.carModel);
      } catch (error) {
        console.error("Failed to fetch car:", error);
        setCar(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCar();
  }, [id]);

  const handleImageClick = (images: string[], index: number) => {
    setSelectedImages(images);
    setStartIndex(index);
    setOpen(true);
  };

  // Detail Section Component
  const DetailSection = ({
    title,
    items,
    color = "primary",
    icon
  }: {
    title: string;
    items: string[];
    color?: "primary" | "secondary" | "success" | "warning" | "info";
    icon?: React.ReactNode;
  }) => (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        {icon}
        <Typography
          variant="subtitle1"
          fontWeight={600}
          color="text.primary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {title}
        </Typography>
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {items?.map((item, idx) => (
          <Chip
            key={idx}
            label={item}
            color={color}
            size={(isMobile || isTablet) ? "small" : "medium"}
            variant="filled"
            sx={{
              borderRadius: 1,
              fontWeight: 500,
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              height: { xs: 24, sm: 32 }
            }}
          />
        ))}
      </Stack>
    </Box>
  );

  const sliderSettings = {
    initialSlide: startIndex,
    dots: true,
    infinite: true,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    autoplay: false,
  };

  // --- Loading/Error States ---
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} />
        </Box>
      </Container>
    );
  }

  if (!car) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="error">
            Vehicle not found
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Box sx={{
        mb: 2,
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap'
      }}>
        <Brandcrumb />
        <Chip
          onClick={() => navigate(-1)}
          icon={<ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />}
          label="Back"
          variant="filled"
          color="secondary"
          sx={{
            fontSize: { xs: '0.75rem', sm: '14px' },
            color: 'text.secondary',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        />
      </Box>

      {/* Main Card Container */}
      <Card
        elevation={4}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {/* Header Section: Logo, Brand, Model Name, and Action Button */}
        <Box sx={{
          p: { xs: 2, sm: 3 },
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.grey[50],
        }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={{ xs: 2, sm: 2 }}
          >
            {/* LEFT SIDE: Logo and Text Stack */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={{ xs: 2, sm: 3 }}
              width={{ xs: '100%', sm: 'auto' }}
            >
              {/* Logo Avatar */}
              <Avatar
                alt={car.brand.name}
                sx={{
                  width: { xs: 56, sm: 64, md: 72 },
                  height: { xs: 56, sm: 64, md: 72 },
                  flexShrink: 0,
                  '& img': {
                    objectFit: 'contain',
                    padding: '8px'
                  }
                }}
              >
                <Box
                  component="img"
                  src={  car.brand.logo ? (baseUrlImage + car.brand.logo) : 'https://cdn-icons-png.freepik.com/512/9942/9942310.png' }
                  alt={`${car.brand.name} Logo`}
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Avatar>

              {/* Car Name/Brand Text */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  fontWeight={500}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {car.brand.parentCompany}
                </Typography>
                <Typography
                  variant={(isMobile || isTablet) ? "h6" : "h3"}
                  fontWeight={500}
                  color="text.primary"
                  lineHeight={1.1}
                  sx={{
                    fontSize: {
                      xs: '1.25rem',
                      sm: '1.5rem',
                      md: '2rem'
                    },
                    wordBreak: 'break-word'
                  }}
                >
                  {car.brand.name} {car.name}
                </Typography>
              </Box>
            </Stack>

            {/* RIGHT SIDE: Action Buttons */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'space-between', sm: 'flex-end' }
            }}>
              <Tooltip title="Add Product for this Car">
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/inventory/addProduct', {
                    state: {
                      carId: car._id,
                      source: 'car-details'
                    }
                  })}
                  size={(isMobile || isTablet) ? "small" : "small"}
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    minWidth: { xs: 'auto', sm: 120 }
                  }}
                >
                  {(isMobile || isTablet) ? 'Add Product' : 'Add Product'}
                </Button>
              </Tooltip>
              <Tooltip title="Edit Car">
                <IconButton
                  color="primary"
                  aria-label="edit car info"
                  onClick={() => navigate(`/configuration/cars/editCar/${car._id}`)}
                  size={(isMobile || isTablet) ? "small" : "medium"}
                >
                  <EditIcon fontSize={(isMobile || isTablet) ? "small" : "inherit"} />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </Box>

        {/* Main Content: Base Image & Specifications */}
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 3, md: 4 }}
            alignItems="flex-start"
          >
            {/* Left Column: Base Model Image */}
            <Box sx={{
              width: { xs: '100%', md: '60%' },
              order: { xs: 1, md: 1 }
            }}>
              <Paper
                elevation={8}
                sx={{
                  borderRadius: 1,
                  overflow: 'hidden',
                  height: '100%'
                }}
              >
                <Box
                  component="img"
                  src={car.baseImage ? baseUrlImage+ car.baseImage : shadowImage}
                  alt={`${car.name} base model`}
                  sx={{
                    width: "100%",
                    height: { xs: 250, sm: 350, md: 400 },
                    objectFit: "contain",
                    p: { xs: 1, sm: 2 },
                    bgcolor: theme.palette.grey[100],
                  }}
                />
              </Paper>
            </Box>

            {/* Right Column: Specifications */}
            <Box sx={{
              width: { xs: '100%', md: '40%' },
              order: { xs: 2, md: 2 }
            }}>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  mb: 3,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}
              >
                Specifications
              </Typography>

              <DetailSection
                title="Available Variants"
                items={car.variants}
                color="info"
                icon={<DirectionsCar sx={{ color: 'info.main', fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
              />

              <DetailSection
                title="Fuel Types"
                items={car.fuelTypes}
                color="success"
                icon={<LocalGasStation sx={{ color: 'success.main', fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
              />

              <DetailSection
                title="Transmissions"
                items={car.transmissions}
                color="secondary"
                icon={<Settings sx={{ color: 'secondary.main', fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
              />
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ my: 0 }} />

        {/* Model Generations Section */}
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <CalendarToday
              color="primary"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            />
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
              Model Generations
            </Typography>
          </Stack>

          <Stack spacing={3}>
            {car.generations?.map((gen, gIndex) => (
              <Paper
                key={gIndex}
                variant="outlined"
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 1,
                  borderColor: theme.palette.grey[300],
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 2, sm: 3 }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  {/* Generation Info */}
                  <Box sx={{
                    flexShrink: 0,
                    minWidth: { sm: '150px' },
                    mb: { xs: 1, sm: 0 }
                  }}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color="primary.dark"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Generation {gIndex + 1}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={700}
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {gen.from} - {gen.to}
                    </Typography>
                  </Box>

                  {/* Generation Images */}
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                    justifyContent={{ xs: 'center', sm: 'flex-start' }}
                    sx={{ width: '100%' }}
                  >
                    {gen.images.map((img, idx) => (
                      <Box
                        key={idx}
                        onClick={() => handleImageClick(gen.images, idx)}
                        sx={{
                          width: { xs: 100, sm: 120, md: 150 },
                          height: { xs: 60, sm: 70, md: 90 },
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: "pointer",
                          transition: "transform 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                            boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                          },
                          flexShrink: 0
                        }}
                      >
                        <Box
                          component="img"
                          src={img ? baseUrlImage+img : shadowImage}
                          alt={`Gen-${gIndex}-${idx}`}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>

        {/* Footer with Metadata */}
        <Box sx={{
          p: 2,
          bgcolor: theme.palette.grey[50],
          borderTop: `1px solid ${theme.palette.divider}`
        }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="flex-end"
            spacing={{ xs: 1, sm: 3 }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
            >
              Created: {new Date(car.createdAt).toLocaleDateString()}
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
            >
              Updated: {new Date(car.updatedAt).toLocaleDateString()}
            </Typography>
          </Stack>
        </Box>
      </Card>

      {/* Image Preview Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: { xs: '95vw', sm: '90vw', md: 'lg' }
          }
        }}
      >
        <IconButton
          onClick={() => setOpen(false)}
          sx={{
            position: "absolute",
            top: { xs: 8, sm: 16 },
            right: { xs: 8, sm: 16 },
            zIndex: 2,
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': { bgcolor: 'grey.100' },
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 }
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
        </IconButton>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Slider {...sliderSettings}>
            {selectedImages.map((img, i) => (
              <Box key={i} sx={{ textAlign: 'center', px: { xs: 0.5, sm: 1 } }}>
                <Box
                  component="img"
                  src={img ? baseUrlImage+img : shadowImage}
                  alt={`Preview-${i}`}
                  sx={{
                    width: "100%",
                    maxHeight: { xs: "50vh", sm: "70vh" },
                    objectFit: "contain",
                    borderRadius: 1,
                  }}
                />
              </Box>
            ))}
          </Slider>
        </Box>
      </Dialog>
    </>
  );
};

export default CarInfo;